import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'store/hook';
import { isDarkTheme } from 'utils/theme';
// import { data as sampleData } from './data';
import EpirocMainPageHandler from 'handlers/epiroc.mainpage.handler';
import FiltersBar from './components/FiltersBar';
import {
  type IndividualAssetData,
  type IndividualLocationData,
} from './interface';
import { stringToHexColor, generateShiftData } from 'utils/glencore';
import TimelineChart from './components/TimelineChart';
import { Grid, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { setFilters } from 'store/epiroc.mainpage.slice';
import EventsLineChart from './components/EventsLineChart';

dayjs.extend(utc);

const Glencore = () => {
  const epirocMainPageHandler = new EpirocMainPageHandler();
  const { theme } = useAppSelector((state) => state.authReducer.customer);
  const enableDarkTheme = isDarkTheme(theme);

  const { accessToken } = useAppSelector((state) => state.authReducer);

  const { startDate, endDate } = useAppSelector(
    (state) => state.epirocMainPageReducer.filters
  );

  const dispatch = useDispatch();

  const [selectedStartDate, setSelectedStartDate] = useState(
    // dayjs().startOf('day').subtract(2, 'days').toDate()
    // 1st march to 3rd march
    dayjs(startDate).toDate()
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    dayjs(endDate).toDate()
    // dayjs().endOf('day').toDate()
  );

  const handleDateChange = (range: any) => {
    const [startDate, endDate] = range;
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
    dispatch(
      setFilters({
        startDate: dayjs(startDate).format('YYYY-MM-DD'),
        endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
      })
    );
  };

  const [data, setData] = useState<IndividualAssetData[]>([]);

  const [locationData, setLocationData] = useState<IndividualLocationData[]>(
    []
  );

  const [loading, setLoading] = useState(false);

  const locations = useMemo(() => {
    const locations = new Set(data.map((item) => item?.location));
    return Array.from(locations);
  }, [data]);

  const assets = useMemo(() => {
    const assets = new Set(locationData.map((item) => item?.asset_base));
    return Array.from(assets);
  }, [locationData]);

  // Memoized location color map
  const locationColors = useMemo(() => {
    const colors: Record<string, string> = {};
    locations.forEach((location) => {
      colors[location] = stringToHexColor(location);
    });
    return colors;
  }, [locations]);

  const [selectedLocations, setSelectedLocations] =
    useState<string[]>(locations);
  const [selectedAssets, setSelectedAssets] = useState<string[]>(assets);

  const handleLocationAdd = (locations: string[]) => {
    if (locations?.length === 0) return;
    setSelectedLocations(locations);
  };

  const handleAssetAdd = (assets: string[]) => {
    if (assets?.length === 0) return;
    setSelectedAssets(assets);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const cachedKey = `asset_timeline_${dayjs(selectedStartDate).format(
        'YYYY-MM-DD'
      )}_${dayjs(selectedEndDate).format('YYYY-MM-DD')}`;
      const cachedData = sessionStorage.getItem(cachedKey);
      if (cachedData && cachedData !== 'undefined') {
        setData(JSON.parse(cachedData));
        setLoading(false);
        return;
      }
      const response = await epirocMainPageHandler.getAssetTimeline(
        dayjs(selectedStartDate).format('YYYY-MM-DD'),
        dayjs(selectedEndDate).format('YYYY-MM-DD')
      );
      setData(response);
      sessionStorage.setItem(cachedKey, JSON.stringify(response));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationData = async () => {
    try {
      setLoading(true);
      const cachedKey = `location_timeline_${dayjs(selectedStartDate).format(
        'YYYY-MM-DD'
      )}_${dayjs(selectedEndDate).format('YYYY-MM-DD')}`;
      const cachedData = sessionStorage.getItem(cachedKey);
      if (cachedData && cachedData !== 'undefined') {
        setLocationData(JSON.parse(cachedData));
        setLoading(false);
        return;
      }
      const response = await epirocMainPageHandler.getLocationTimeline(
        dayjs(selectedStartDate).format('YYYY-MM-DD'),
        dayjs(selectedEndDate).format('YYYY-MM-DD')
      );
      setLocationData(response);
      sessionStorage.setItem(cachedKey, JSON.stringify(response));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchClick = () => {
    const promises = [fetchData(), fetchLocationData()];
    void Promise.all(promises);
  };

  useEffect(() => {
    if (!accessToken) return;
    const promises = [fetchData(), fetchLocationData()];
    void Promise.all(promises);
  }, [accessToken]);

  useEffect(() => {
    setSelectedLocations(locations);
  }, [locations]);

  useEffect(() => {
    setSelectedAssets(assets);
  }, [assets]);

  const activityOptions = ['Bolt', 'Drill', 'Mucking'];
  const batteryOptions = [
    'Battery Energy Drained',
    'Change in Battery Charge',
    'Battery Accumulated Energy',
  ];
  const [selectedActivities, setSelectedActivities] =
    useState<string[]>(activityOptions);
  const [selectedBatteryInsights, setSelectedBatteryInsights] =
    useState<string[]>(batteryOptions);
  const handleActivitiesChange = (opts: string[]) => {
    setSelectedActivities(opts);
  };
  const handleBatteryInsightsChange = (opts: string[]) => {
    setSelectedBatteryInsights(opts);
  };

  const [timelineView, setTimelineView] = useState<'location' | 'asset'>(
    'location'
  );

  const timelineData = useMemo(() => {
    const baseData = data?.filter((item) => {
      if (timelineView === 'asset') {
        return selectedLocations.includes(item?.location);
      }
      return true;
    });
    const locationEventData = {
      name: 'Location',
      data: baseData?.map((item) => ({
        x: item?.asset_base,
        y: [
          dayjs.utc(item?.start_ts).valueOf(),
          dayjs.utc(item?.end_ts).valueOf(),
        ],
        location_name: item?.location,
        fillColor: locationColors[item?.location],
        duration: item?.duration_hours * 60,
        total_work_hours: item?.total_work_hours,
        asset_base: item?.asset_base,
        start_ts: item?.start_ts,
        end_ts: item?.end_ts,
      })),
    };
    const boltEventsData = {
      name: 'Bolt',
      color: '#3F51B5',
      data: baseData
        ?.filter((item) => item?.event_type === 'bolt')
        ?.map((item) => [
          {
            x: item?.asset_base,
            y: [
              dayjs.utc(item?.first_event_ts).valueOf(),
              dayjs.utc(item?.last_event_ts).valueOf(),
            ],
            bolt_count: item?.bolt_count_increase,
            fillColor: stringToHexColor('Bolt'),
            duration: item?.event_duration_minute,
            location_name: item?.location,
            asset_base: item?.asset_base,
            start_ts: item?.first_event_ts,
            end_ts: item?.last_event_ts,
          },
          {
            x: item?.asset_base,
            y: [
              dayjs.utc(item?.mobilization_start).valueOf(),
              dayjs.utc(item?.mobilization_end).valueOf(),
            ],
            type: 'Mobilization',
            fillColor: '#4CAF50',
            duration: item?.mobilization_duration_minute,
            location_name: item?.location,
            asset_base: item?.asset_base,
            start_ts: item?.mobilization_start,
            end_ts: item?.mobilization_end,
          },
          {
            x: item?.asset_base,
            y: [
              dayjs.utc(item?.demobilization_start).valueOf(),
              dayjs.utc(item?.demobilization_end).valueOf(),
            ],
            type: 'Demobilization',
            fillColor: '#F44336',
            duration: item?.demobilization_duration_minute,
            location_name: item?.location,
            asset_base: item?.asset_base,
            start_ts: item?.demobilization_start,
            end_ts: item?.demobilization_end,
          },
        ])
        .flatMap((item) => item),
    };
    const drillEventsData = {
      name: 'Drill',
      color: stringToHexColor('Drill'),
      data: baseData
        ?.filter(
          (item) =>
            item?.event_type === 'drill' || item?.event_type === 'drill2'
        )
        ?.map((item) => [
          {
            x: item?.asset_base,
            y: [
              dayjs.utc(item?.first_event_ts).valueOf(),
              dayjs.utc(item?.last_event_ts).valueOf(),
            ],
            fillColor: stringToHexColor('Drill'),
            drill_meter:
              item?.[
                +item?.drill_meter_increase > 0
                  ? 'drill_meter_increase'
                  : 'drill2_meter_increase'
              ],
            duration: item?.event_duration_minute,
            location_name: item?.location,
            asset_base: item?.asset_base,
            start_ts: item?.first_event_ts,
            end_ts: item?.last_event_ts,
          },
          {
            x: item?.asset_base,
            y: [
              dayjs.utc(item?.mobilization_start).valueOf(),
              dayjs.utc(item?.mobilization_end).valueOf(),
            ],
            type: 'Mobilization',
            fillColor: '#4CAF50',
            duration: item?.mobilization_duration_minute,
            asset_base: item?.asset_base,
            location_name: item?.location,
            start_ts: item?.mobilization_start,
            end_ts: item?.mobilization_end,
          },
          {
            x: item?.asset_base,
            y: [
              dayjs.utc(item?.demobilization_start).valueOf(),
              dayjs.utc(item?.demobilization_end).valueOf(),
            ],
            type: 'Demobilization',
            fillColor: '#F44336',
            duration: item?.demobilization_duration_minute,
            asset_base: item?.asset_base,
            location_name: item?.location,
            start_ts: item?.demobilization_start,
            end_ts: item?.demobilization_end,
          },
        ])
        .flatMap((item) => item),
    };

    const bucketEventsData = {
      name: 'Mucking',
      color: '#FFC107',
      data: baseData
        ?.filter((item) => item?.event_type === 'bucket')
        ?.map((item) => [
          {
            x: item?.asset_base,
            y: [
              dayjs.utc(item?.first_event_ts).valueOf(),
              dayjs.utc(item?.last_event_ts).valueOf(),
            ],
            bucket_count: item?.total_loaded_buckets,
            fillColor: stringToHexColor('Mucking'),
            duration: item?.event_duration_minute,
            location_name: item?.location,
            asset_base: item?.asset_base,
            start_ts: item?.first_event_ts,
            end_ts: item?.last_event_ts,
          },
          {
            x: item?.asset_base,
            y: [
              dayjs.utc(item?.mobilization_start).valueOf(),
              dayjs.utc(item?.mobilization_end).valueOf(),
            ],
            type: 'Mobilization',
            fillColor: '#4CAF50',
            duration: item?.mobilization_duration_minute,
            location_name: item?.location,
            asset_base: item?.asset_base,
            start_ts: item?.mobilization_start,
            end_ts: item?.mobilization_end,
          },
          {
            x: item?.asset_base,
            y: [
              dayjs.utc(item?.demobilization_start).valueOf(),
              dayjs.utc(item?.demobilization_end).valueOf(),
            ],
            type: 'Demobilization',
            fillColor: '#F44336',
            duration: item?.demobilization_duration_minute,
            location_name: item?.location,
            asset_base: item?.asset_base,
            start_ts: item?.demobilization_start,
            end_ts: item?.demobilization_end,
          },
        ])
        .flatMap((item) => item),
    };

    const batteryEnergyDrainedData = {
      name: 'Battery Energy Drained',
      data: baseData
        ?.filter((item) => item?.event_type === 'battery_energy_drained')
        ?.map((item) => ({
          x: item?.asset_base,
          y: [
            dayjs.utc(item?.start_ts).valueOf(),
            dayjs.utc(item?.end_ts).valueOf(),
          ],
          fillColor: '#f2765d', // color for battery energy drained to represent battery energy drained
          min_battery_energy_drained: item?.min_battery_energy_drained,
          max_battery_energy_drained: item?.max_battery_energy_drained,
          duration: item?.event_duration_minute,
          location_name: item?.location,
          asset_base: item?.asset_base,
          start_ts: item?.start_ts,
          end_ts: item?.end_ts,
        })),
    };

    const batteryAccumulatedEnergyData = {
      name: 'Battery Accumulated Energy',
      data: baseData
        ?.filter((item) => item?.event_type === 'battery_accumulated_energy')
        ?.map((item) => ({
          x: item?.asset_base,
          y: [
            dayjs.utc(item?.start_ts).valueOf(),
            dayjs.utc(item?.end_ts).valueOf(),
          ],
          fillColor: '#f2af11', // color for battery accumulated energy to represent battery accumulated energy
          battery_accumulated_energy: item?.battery_accumulated_energy,
          min_battery_accumulated_energy: item?.min_battery_accumulated_energy,
          max_battery_accumulated_energy: item?.max_battery_accumulated_energy,
          duration: item?.event_duration_minute,
          location_name: item?.location,
          asset_base: item?.asset_base,
          start_ts: item?.start_ts,
          end_ts: item?.end_ts,
        })),
    };

    const batteryChargeStatusData = {
      name: 'Battery Charge Status',
      color: '#81D4FA',
      data: baseData
        ?.filter((item) => item?.event_type === 'battery_charge_status')
        ?.map((item) => ({
          x: item?.asset_base,
          y: [
            dayjs.utc(item?.start_ts).valueOf(),
            dayjs.utc(item?.end_ts).valueOf(),
          ],
          fillColor: '#81d4fa', // color for Battery Charge Status to represent Battery Charge Status
          battery_charge_status: item?.battery_charge_status,
          min_battery_charge_status: item?.min_battery_charge_status,
          max_battery_charge_status: item?.max_battery_charge_status,
          duration: item?.event_duration_minute,
          location_name: item?.location,
          asset_base: item?.asset_base,
          start_ts: item?.start_ts,
          end_ts: item?.end_ts,
        })),
    };

    const allSeries = [
      locationEventData,
      boltEventsData,
      drillEventsData,
      bucketEventsData,
      batteryEnergyDrainedData,
      batteryAccumulatedEnergyData,
      batteryChargeStatusData,
    ];
    return allSeries.filter((s) => {
      if (['Bolt', 'Drill', 'Bucket'].includes(s.name))
        return selectedActivities.includes(s.name);
      if (
        [
          'Battery Energy Drained',
          'Change in Battery Charge',
          'Battery Accumulated Energy',
        ].includes(s.name)
      )
        return selectedBatteryInsights.includes(s.name);
      return true;
    });
  }, [
    timelineView,
    data,
    selectedLocations,
    locationColors,
    selectedActivities,
    selectedBatteryInsights,
  ]);

  const locationTimeline = useMemo(() => {
    const assets = Array.from(
      new Set(locationData.map((l) => l.asset_base))
    )?.filter((asset) => {
      if (timelineView === 'location') {
        return selectedAssets.includes(asset);
      }
      return true;
    });
    const assetSeries = assets.map((asset) => ({
      name: asset,
      color: stringToHexColor(``),
      data: locationData
        .filter((l) => l.asset_base === asset)
        .map((l) => ({
          x: l.location,
          y: [dayjs.utc(l.start_ts).valueOf(), dayjs.utc(l.end_ts).valueOf()],
          fillColor:
            !l?.drill_meter_increase &&
            !l?.drill2_meter_increase &&
            !l?.total_loaded_buckets &&
            !l?.bolt_count_increase
              ? '#a30000ff'
              : stringToHexColor(
                  l?.total_loaded_buckets
                    ? 'Bucket'
                    : l?.drill2_meter_increase >= 0 ||
                      l?.drill_meter_increase >= 0
                    ? 'Drill'
                    : l?.bolt_count_increase >= 0
                    ? 'Bolt'
                    : 'Bucket'
                ),
          duration: l.duration_hours * 60,
          bolt_count: l?.bolt_count_increase,
          bucket_count: l?.total_loaded_buckets,
          drill_meter: l?.drill_meter_increase || l?.drill2_meter_increase,
          battery_energy_drained: l?.battery_energy_drained,
          min_battery_energy_drained: l?.min_battery_energy_drained,
          max_battery_energy_drained: l?.max_battery_energy_drained,
          battery_accumulated_energy: l?.battery_accumulated_energy,
          min_battery_accumulated_energy: l?.min_battery_accumulated_energy,
          max_battery_accumulated_energy: l?.max_battery_accumulated_energy,
          battery_charge_status: l?.battery_charge_status,
          min_battery_charge_status: l?.min_battery_charge_status,
          max_battery_charge_status: l?.max_battery_charge_status,
          type: '-',
          start_ts: l.start_ts,
          end_ts: l.end_ts,
          asset_base: l.asset_base,
          location: l.location,
        })),
    }));
    return [...assetSeries];
  }, [locationData, selectedLocations, selectedAssets, timelineView]);

  const annotations = useMemo(() => {
    if (!selectedStartDate || !selectedEndDate) return {};
    const annotations: ApexAnnotations = {
      xaxis: generateShiftData(selectedStartDate, selectedEndDate).map(
        (item) => ({
          x: item.y[0],
          x2: item.y[1],
          fillColor: stringToHexColor(
            `${item.type}_${enableDarkTheme ? 'dark' : 'light'}`
          ),
          opacity: 0.2,
          borderColor: stringToHexColor(
            `${item.type}_${enableDarkTheme ? 'dark' : 'light'}`
          ),
          borderWidth: 1,
        })
      ),
    };
    return annotations;
  }, [selectedStartDate, selectedEndDate, enableDarkTheme]);

  const [hoveredLocation] = useState<string | null>(null);

  const chartSeries = useMemo(() => {
    if (!hoveredLocation) return timelineData;
    const dimColor = '#cccccc33';
    return timelineData.map((seriesItem: any) => {
      if (seriesItem.name === 'Location') {
        const newData = seriesItem.data.map((pt: any) =>
          pt.location_name === hoveredLocation
            ? pt
            : { ...pt, fillColor: dimColor, opacity: 0.2, borderWidth: 0 }
        );
        return {
          ...seriesItem,
          data: newData,
          opacity: 0.2,
          borderWidth: 0,
          color: dimColor,
        };
      }
      const newData = seriesItem.data.map((pt: any) => ({
        ...pt,
        fillColor: dimColor,
        opacity: 0.2,
        borderWidth: 0,
      }));
      return {
        ...seriesItem,
        data: newData,
        opacity: 0.2,
        borderWidth: 0,
        color: dimColor,
      };
    });
  }, [timelineData, hoveredLocation]);

  const [toggleLegendOpen, setToggleLegendOpen] = useState(false);

  const handleToggleLegend = () => {
    setToggleLegendOpen((prev) => !prev);
  };

  const [
    selectedAssetLocationAndTimestamps,
    setSelectedAssetLocationAndTimestamps,
  ] = useState<{
    assetBase: string;
    location: string;
    start_ts: string;
    end_ts: string;
  } | null>(null);

  useEffect(() => {
    setSelectedAssetLocationAndTimestamps(null);
  }, [timelineView]);

  return (
    <div className="w-full h-full">
      <FiltersBar
        locations={locations}
        selectedLocations={selectedLocations}
        onLocationsChange={handleLocationAdd}
        activityOptions={activityOptions}
        selectedActivities={selectedActivities}
        onActivitiesChange={handleActivitiesChange}
        batteryOptions={batteryOptions}
        selectedBatteryInsights={selectedBatteryInsights}
        onBatteryInsightsChange={handleBatteryInsightsChange}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
        onDateChange={handleDateChange}
        loading={loading}
        onFetch={handleFetchClick}
        darkMode={enableDarkTheme}
        timelineView={timelineView}
        onTimelineViewChange={setTimelineView}
        locationColors={locationColors}
        // assetColors={assetColors}
        assets={assets}
        selectedAssets={selectedAssets}
        onAssetsChange={handleAssetAdd}
      />
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          md={toggleLegendOpen ? 8 : 12}
          className="relative flex justify-end items-start"
        >
          {timelineView === 'location' && (
            <TimelineChart
              series={locationTimeline}
              annotations={annotations}
              isDark={enableDarkTheme}
              loading={loading}
              height={900}
              width="100%"
              setSelectedAssetLocationAndTimestamps={
                setSelectedAssetLocationAndTimestamps
              }
              setToggleLegendOpen={setToggleLegendOpen}
              timelineView={timelineView}
            />
          )}
          {timelineView === 'asset' && (
            <TimelineChart
              series={chartSeries}
              annotations={annotations}
              isDark={enableDarkTheme}
              loading={loading}
              height={900}
              width="100%"
              setSelectedAssetLocationAndTimestamps={
                setSelectedAssetLocationAndTimestamps
              }
              setToggleLegendOpen={setToggleLegendOpen}
              timelineView={timelineView}
            />
          )}
          <IconButton
            className="!absolute !top-10 !right-0"
            onClick={handleToggleLegend}
            disableRipple
            sx={{
              color: enableDarkTheme ? '#fff' : '#000',
            }}
          >
            {toggleLegendOpen ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Grid>
        {toggleLegendOpen && (
          <Grid item xs={12} md={4} sx={{ overflow: 'hidden' }}>
            <EventsLineChart
              height={timelineView === 'location' ? 650 : 900}
              insightsContainerHeight={timelineView === 'location' ? 460 : 710}
              assetBase={selectedAssetLocationAndTimestamps?.assetBase ?? ''}
              location={selectedAssetLocationAndTimestamps?.location ?? ''}
              startDate={selectedAssetLocationAndTimestamps?.start_ts ?? ''}
              endDate={selectedAssetLocationAndTimestamps?.end_ts ?? ''}
            />
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default Glencore;
